/**
 * Frente C — helpers puros da governança por-etapa do editor de Playbook.
 *
 * Espelham a semântica do parser canônico do motor
 * (`apps/engine/src/modules/ai-orchestration/ai-conductor/step-governance.ts`)
 * SEM reimplementá-la: a UI só torna a regra de arme do gate LEGÍVEL ao operador
 * (o motor reavalia no runtime). Setters são OMIT-ON-DEFAULT — devolvem a etapa
 * sem a chave quando o valor volta ao default, para o dirty-tracking do
 * `buildPatch` (jsonEq contra o wire cru) não abrir a tela "suja". Molde:
 * `buildTriggersConfig`/`buildVerificationConfig` ("só divergências").
 */

import type { PlaybookStep, StepConfirmation, StepEnforcement, StepObligation } from './types';

export const DEFAULT_ENFORCEMENT: StepEnforcement = 'flexivel';
export const DEFAULT_OBLIGATION: StepObligation = 'optional';
export const DEFAULT_CONFIRMATION: StepConfirmation = 'none';

/**
 * Bloqueio terminal default que o motor aplica a uma etapa dura sem `blocks`
 * explícito. Espelha `DEFAULT_GATE_BLOCKS` do parser — usado só para o hint da
 * UI ("vazio = bloqueia escalar e encerrar").
 */
export const DEFAULT_GATE_BLOCKED_ACTIONS: readonly string[] = ['escalate', 'close_conversation'];

/** Remove uma chave opcional da etapa preservando o resto (imutável). */
function without(step: PlaybookStep, key: keyof PlaybookStep): PlaybookStep {
  const next = { ...step };
  delete next[key];
  return next;
}

/**
 * Muda a força E poda os eixos que a nova força não expõe no editor — senão o
 * operador deixaria dado órfão INALCANÇÁVEL (não conseguiria limpá-lo depois,
 * pois os controles ficam escondidos). Semântica:
 *  - `flexivel` (Sugestão) = sem governança → limpa todos os eixos de detalhe;
 *  - `guiado` = sem gate de código → limpa sinal + bloqueios (duro-only);
 *  - `duro` = mantém tudo.
 */
export function setEnforcement(step: PlaybookStep, value: StepEnforcement): PlaybookStep {
  let next =
    value === DEFAULT_ENFORCEMENT ? without(step, 'enforcement') : { ...step, enforcement: value };
  if (value === 'flexivel') {
    next = without(next, 'obligation');
    next = without(next, 'confirmation');
    next = without(next, 'successSignal');
    next = without(next, 'blocks');
  } else if (value === 'guiado') {
    next = without(next, 'successSignal');
    next = without(next, 'blocks');
  }
  return next;
}

export function setObligation(step: PlaybookStep, value: StepObligation): PlaybookStep {
  return value === DEFAULT_OBLIGATION
    ? without(step, 'obligation')
    : { ...step, obligation: value };
}

export function setConfirmation(step: PlaybookStep, value: StepConfirmation): PlaybookStep {
  return value === DEFAULT_CONFIRMATION
    ? without(step, 'confirmation')
    : { ...step, confirmation: value };
}

/** Texto livre → `successSignal` (só `tool-invoked`). Vazio ⇒ remove o sinal. */
export function setSuccessTool(step: PlaybookStep, tool: string): PlaybookStep {
  const trimmed = tool.trim();
  return trimmed === ''
    ? without(step, 'successSignal')
    : { ...step, successSignal: { type: 'tool-invoked', tool: trimmed } };
}

/**
 * Normaliza `blocks` a partir das duas listas. Ambas vazias ⇒ remove `blocks`
 * (o parser colapsa vazio→terminal default de qualquer forma). Molde do guard
 * anti-footgun do motor.
 */
function normalizeBlocks(step: PlaybookStep, actions: string[], tools: string[]): PlaybookStep {
  if (actions.length === 0 && tools.length === 0) return without(step, 'blocks');
  return { ...step, blocks: { actions, tools } };
}

export function setBlockedActions(step: PlaybookStep, actions: string[]): PlaybookStep {
  return normalizeBlocks(step, actions, step.blocks?.tools ?? []);
}

export function setBlockedTools(step: PlaybookStep, tools: string[]): PlaybookStep {
  return normalizeBlocks(step, step.blocks?.actions ?? [], tools);
}

/** Objetivo não-vazio: pré-condição do parser (`parseStep` DESCARTA a etapa sem). */
function hasObjective(step: PlaybookStep): boolean {
  return typeof step.objective === 'string' && step.objective.trim() !== '';
}

/** `tool` do sinal de forma segura (o wire pode trazer `{type}` sem `tool`). */
function signalTool(step: PlaybookStep): string {
  return step.successSignal?.tool?.trim() ?? '';
}

/**
 * A etapa "arma" o gate duro sse, e só se: TEM objetivo (senão o parser descarta
 * a etapa inteira) ∧ força=duro ∧ obrigatoriedade≠opcional ∧ há sinal
 * determinístico (ferramenta não-vazia). Espelha `parseStep` + `isGatingStep`.
 */
export function stepArmsGate(step: PlaybookStep): boolean {
  return (
    hasObjective(step) &&
    step.enforcement === 'duro' &&
    step.obligation !== undefined &&
    step.obligation !== 'optional' &&
    signalTool(step) !== ''
  );
}

/**
 * Sob força=duro, o que ainda falta para a trava armar. Vazio ⇒ arma. Usado no
 * banner de status honesto (evita o footgun "marquei duro mas nada bloqueia").
 * Fora de `duro`, retorna `[]` (a pergunta só faz sentido sob duro).
 */
export function gateGaps(step: PlaybookStep): string[] {
  if (step.enforcement !== 'duro') return [];
  const gaps: string[] = [];
  if (!hasObjective(step)) gaps.push('objetivo da etapa');
  if (step.obligation === undefined || step.obligation === 'optional') {
    gaps.push('obrigatoriedade obrigatória ou condicional');
  }
  if (signalTool(step) === '') {
    gaps.push('sinal de sucesso (ferramenta)');
  }
  return gaps;
}

/**
 * Lista efetiva de ações/ferramentas bloqueadas por uma etapa dura. Tolera
 * `blocks` parcial (`{actions}` sem `tools`) — o wire/seed traz assim. Vazio
 * (ou ausente) ⇒ default terminal, espelhando o guard anti-footgun do parser.
 */
export function effectiveBlockedList(step: PlaybookStep): string[] {
  const raw =
    step.blocks != null ? [...(step.blocks.actions ?? []), ...(step.blocks.tools ?? [])] : [];
  return raw.length > 0 ? raw : [...DEFAULT_GATE_BLOCKED_ACTIONS];
}

/** A etapa tem algum eixo de governança não-default (abre a seção já aberta). */
export function hasGovernance(step: PlaybookStep): boolean {
  return (
    (step.enforcement !== undefined && step.enforcement !== DEFAULT_ENFORCEMENT) ||
    (step.obligation !== undefined && step.obligation !== DEFAULT_OBLIGATION) ||
    (step.confirmation !== undefined && step.confirmation !== DEFAULT_CONFIRMATION) ||
    step.successSignal != null ||
    step.blocks != null
  );
}
